import {
  buildRoomLink,
  type Config,
  loadConfig,
  parseRoomLink,
  type RoomLink,
} from "@codeshare/config";
import { newRoomSecret } from "@codeshare/crypto";
import type { AppInfo } from "@codeshare/protocol";
import { type Profile, type RemotePeer, Room } from "@codeshare/session";

export type RoomStatus = "solo" | "joining" | "joined" | "error";

export interface RoomState {
  status: RoomStatus;
  /** Shareable link while in a room. */
  link?: string;
  peers: RemotePeer[];
  /** People this device repeatedly couldn't connect to (see Room.unreachable). */
  unreachable?: number;
  error?: string;
  warnings: string[];
}

export interface RoomControllerOptions {
  app: AppInfo;
  profile: Profile;
  /** Capability flags announced in hello (e.g. "run:python"), for features peers may lack. */
  caps?: string[];
  /** Page URL (without fragment) used as the base of share links. */
  baseUrl: string;
  loadConfig?: (link?: RoomLink) => Promise<{ config: Config; warnings: string[] }>;
  /** Writes the room fragment to the address bar without navigating. */
  setFragment?: (fragment: string) => void;
  /** Called once the room is joined. `created`: this user started the room by sharing. */
  onJoined?: (room: Room, created: boolean) => void | Promise<void>;
  /** Called after leaving a room. */
  onLeft?: () => void;
  /** Called instead of `onLeft` after "Leave & forget": delete what's stored for the room. */
  onForgotten?: (topic: string) => void | Promise<void>;
}

/**
 * Owns the app's room lifecycle: joining from a link, creating a room to share, leaving, and an
 * observable state for the UI.
 */
export class RoomController {
  #options: Required<
    Omit<RoomControllerOptions, "setFragment" | "onJoined" | "onLeft" | "onForgotten">
  > &
    Pick<RoomControllerOptions, "setFragment" | "onJoined" | "onLeft" | "onForgotten">;
  readonly #listeners = new Set<() => void>();
  #state: RoomState = { status: "solo", peers: [], warnings: [] };
  #room: Room | undefined;
  #config: Config | undefined;
  /** A room being created by share(); a second share() waits for it instead of making another. */
  #sharing: Promise<string> | undefined;
  /** A join in progress; leaving waits for it so none outlives the leave. */
  #joining: Promise<void> | undefined;
  /** Bumped by every leave: a join started before it stops at its next step. */
  #epoch = 0;
  #unsubscribe: (() => void) | undefined;

  constructor(options: RoomControllerOptions) {
    this.#options = {
      caps: [],
      loadConfig: (link) =>
        loadConfig({
          env: import.meta.env,
          fetch: (url, init) => fetch(url, { ...init, cache: "no-store" }),
          configUrl: `${import.meta.env.BASE_URL}config.json`,
          storage: globalThis.localStorage,
          ...(link ? { link } : {}),
        }),
      ...options,
    };
  }

  get state(): RoomState {
    return this.#state;
  }

  get room(): Room | undefined {
    return this.#room;
  }

  /** The config the current room was joined with. */
  get config(): Config | undefined {
    return this.#config;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Joins the room in a URL/fragment, if it has one. */
  async joinFromUrl(url: string): Promise<boolean> {
    const link = parseRoomLink(url);
    if (!link) return false;
    if (this.#state.link && parseRoomLink(this.#state.link)?.secret === link.secret) return true;
    await this.#join(link, false);
    return true;
  }

  /**
   * Creates a new room (or reuses the current one, including one still starting) and returns its
   * shareable link.
   */
  share(): Promise<string> {
    if (this.#sharing) return this.#sharing;
    if (this.#state.link && this.#state.status !== "error")
      return Promise.resolve(this.#state.link);
    return this.#track(this.#createRoom());
  }

  /** Leaves the current room and starts a new one (e.g. with new connection settings). */
  restart(): Promise<string> {
    return this.#track(this.leave().then(() => this.#createRoom()));
  }

  /** While a room is being created, share() waits for it instead of making another. */
  #track(creating: Promise<string>): Promise<string> {
    const tracked = creating.finally(() => {
      if (this.#sharing === tracked) this.#sharing = undefined;
    });
    this.#sharing = tracked;
    return tracked;
  }

  async #createRoom(): Promise<string> {
    const epoch = this.#epoch;
    const { config } = await this.#options.loadConfig();
    if (epoch !== this.#epoch) return ""; // left meanwhile
    await this.#join({ secret: newRoomSecret(), signaling: config.signaling }, true);
    return this.#state.link ?? "";
  }

  setProfile(profile: Profile): void {
    this.#options.profile = profile;
    this.#room?.setProfile(profile);
  }

  /** Leaves the room. `forget`: also delete this device's copy of its files and chat. */
  async leave(options: { forget?: boolean } = {}): Promise<void> {
    this.#epoch++;
    await this.#joining?.catch(() => {});
    this.#unsubscribe?.();
    const wasInRoom = this.#room !== undefined;
    const topic = this.#room?.topic;
    await this.#room?.leave();
    this.#room = undefined;
    this.#options.setFragment?.("");
    this.#set({ status: "solo", peers: [], warnings: [] });
    if (!wasInRoom) return;
    if (options.forget && topic) await this.#options.onForgotten?.(topic);
    else this.#options.onLeft?.();
  }

  async #join(link: RoomLink, created: boolean): Promise<void> {
    // Replaces any room we're in or joining (leave() stops and waits for a join in progress).
    if (this.#room || this.#joining) await this.leave();
    const attempt = this.#connect(link, created, this.#epoch);
    this.#joining = attempt;
    try {
      await attempt;
    } finally {
      if (this.#joining === attempt) this.#joining = undefined;
    }
  }

  /** The join itself. Stops at its next step (without onJoined) once a leave happened. */
  async #connect(link: RoomLink, created: boolean, epoch: number): Promise<void> {
    const left = () => epoch !== this.#epoch;
    const link_ = buildRoomLink(this.#options.baseUrl, link);
    const showLink = () => this.#options.setFragment?.(link_.slice(link_.indexOf("#")));
    // A link you opened is already in the address bar. A new room's link goes there once the
    // room has your files: reloading before then just starts a new room from them, instead of
    // opening a room that's still empty.
    if (!created) showLink();
    this.#set({ status: "joining", link: link_, peers: [], warnings: [] });
    try {
      const { config, warnings } = await this.#options.loadConfig(link);
      if (left()) return;
      this.#config = config;
      const room = new Room({
        secret: link.secret,
        config,
        profile: this.#options.profile,
        app: this.#options.app,
        caps: this.#options.caps,
      });
      this.#room = room;
      const offPeers = room.onPeersChange((peers) =>
        this.#set({ ...this.#state, peers, unreachable: room.unreachable }),
      );
      const offFull = room.onFull((maxPeers) => {
        if (this.#room !== room) return;
        // A partial mesh would be confusing (some people see you, others don't): don't stay.
        void this.leave().then(() =>
          this.#set({
            status: "error",
            peers: [],
            warnings: [],
            error: `This room is full: a room can have up to ${maxPeers + 1} people.`,
          }),
        );
      });
      this.#unsubscribe = () => {
        offPeers();
        offFull();
      };
      await room.join();
      // Left while connecting: the leave closes this room; it never counts as joined.
      if (left()) return;
      await this.#options.onJoined?.(room, created);
      if (left()) return;
      if (created) showLink();
      this.#set({ ...this.#state, status: "joined", warnings });
    } catch (error) {
      if (!left()) this.#set({ ...this.#state, status: "error", error: String(error) });
    }
  }

  #set(state: RoomState): void {
    this.#state = state;
    for (const listener of this.#listeners) listener();
  }
}
